// Sidebar.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useDatabase } from 'context/DatabaseContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNavigation } from 'context/NavigationContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';
import { useNodePeek } from 'context/NodePeekContext';
import { buildCollectionPath } from 'utilities/collectionPaths';
import { setPendingCreateNodeModule } from 'utilities/createNodeModuleContext';
import { NODE_MODULE } from 'utilities/nodeModule';

// Routing
import { useLocation, useNavigate } from 'react-router-dom';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { PhotoShape, SectionType } from 'utilities/Enums';
import { buildBaseMemberProfilePath, getSectionPath } from 'utilities/Routing';
// Services
import {
    isBaseBillingInGracePeriod,
    isBaseBillingNavigationLocked,
} from 'services/billing/BaseBillingService';

// Hooks
import useSidebarSelection from './hooks/useSidebarSelection';
import useTopLevelNodeCreate from 'hooks/useTopLevelNodeCreate';
import useBaseAccess from 'hooks/useBaseAccess';
import useBaseAccessLock from 'hooks/useBaseAccessLock';
import useBaseCreateModals from './buckets/hooks/useBaseCreateModals';
import useBaseChannels from 'channels/hooks/useBaseChannels';
import { buildAiFeedChannelKey } from 'spaces/utils/aiChannelKeys';
import {
    buildAiHomePath,
    getAiSessionKeyFromLocation,
} from 'spaces/utils/buildAiHomePath';
import { AI_ICON_KEY, AI_ICON_PATH } from 'svg/customIconPaths';
import { formatBaseLockedNotice } from 'utilities/baseAccessCopy';
import { openBaseInvite } from 'utilities/openBaseInvite';
import { openBaseSettings } from 'utilities/openBaseSettings';

// CSS
import './Sidebar.css';

// Utilities (addmodal)
import { isBaseLauncherKey, openBaseLauncher } from 'components/addmodal/utilities/baseLaunchers';

// Components
// import AddNodeButton from 'main/sidebar/switcher/addnode/AddNodeButton';
import SidebarBuckets from './buckets/SidebarBuckets';
import SidebarBucketsModals from './buckets/SidebarBucketsModals';
import AddModal from 'components/addmodal/AddModal';
import Divider from 'components/divider/Divider';
import BaseCover from 'components/basecover/BaseCover';
import CameraModal from 'editor/nodeadd/camera/CameraModal';
import SidebarButton from 'main/sidebar/button/SidebarButton'
import ShellHeader from 'main/shell/ShellHeader';
import UserButton from 'main/sidebar/user/UserButton';
import { getActiveBaseKeyFromSettings } from 'utilities/baseSettingsKeys';
import { HELP_ICON_PATH } from 'utilities/HelpArticles';
import useHelpContext from 'help/hooks/useHelpContext';
import HelpPanel from 'help/panel/HelpPanel';

function formatBillingLockNotice(base, record, lockReason) {

    if (lockReason === 'trial_expired') {
        return formatBaseLockedNotice();
    }

    if (!record) return '';

    const gracePeriodEndsAt = record?.gracePeriodEndsAt instanceof Date
        ? record.gracePeriodEndsAt
        : null;

    if (isBaseBillingNavigationLocked(record)) {
        return 'Billing access is locked. Open base settings to update payment and restore navigation.';
    }

    if (!isBaseBillingInGracePeriod(record)) {
        return '';
    }

    if (!gracePeriodEndsAt) {
        return 'Billing issue detected. Update payment to avoid navigation lock.';
    }

    return `Billing issue detected. Update payment before ${gracePeriodEndsAt.toLocaleString()} to avoid navigation lock.`;
}


/**
 * Sidebar
 * 
 * Renders the desktop menu with navigation and action buttons.
 *
 * @param {boolean} hideAddButton - Hides the create/add node button in the header.
 * @param {boolean} hideTemplatesButton - Hides the Templates shortcut.
 * @param {boolean} forceVisible - Forces the sidebar to remain visible on mobile breakpoints.
 * @param {function} onRequestClose - Requests closing the mobile drawer after sidebar actions.
 * 
 * @returns {ReactNode} Sidebar component.
 */
const Sidebar = React.memo(({
    hideAddButton = false,
    hideTemplatesButton = false,
    hideHeader = false,
    forceVisible = false,
    onRequestClose,
    coverHeight,
}) => {

    // ----- CONTEXT ----- //

    // Global Context

    const { setCurrentNode, setCurrentSection } = useNavigation();
    const { isSidebarRailCompact, setSelectedLocation } = useUI();

    // Theme Context
    const { theme } = useTheme();

    // User Context
    const {
        user,
        userUnreadNotifications,
        userSettings,
    } = useUser();

    // Base Context
    const { activeBase } = useBase();

    // Database Context
    const { setActiveDatabase } = useDatabase();

    // Node Types Context
    const { getType } = useNodeTypes();

    // Language Context
    const language = useCurrentLanguage();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- ROUTING ----- //

    const navigate = useNavigate();
    const location = useLocation();

    // ----- HOOKS ----- //

    const {
        accessLocked: baseNavigationLocked,
        billingRecord,
        lockReason,
    } = useBaseAccessLock();

    const { baseTier, can } = useBaseAccess(activeBase);
    const canSeeBaseSettings = baseTier === 'owner' || baseTier === 'admin';
    const canOpenSettings = canSeeBaseSettings;
    const canInviteMembers = can('invite.create') && !baseNavigationLocked;
    const { openPeek } = useNodePeek();
    const { typeKey: helpTypeKey, typeLabel: helpTypeLabel } = useHelpContext();

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!baseNavigationLocked) {
            return;
        }

        if (lockReason === 'trial_expired') {
            return;
        }

        if (location.pathname === '/') {
            return;
        }

        setActiveDatabase(null);
        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.HOME);
        navigate('/', { replace: true });
    }, [
        lockReason,
        location.pathname,
        navigate,
        setActiveDatabase,
        setCurrentNode,
        setCurrentSection,
        setSelectedLocation,
        baseNavigationLocked,
    ]);

    // ----- MEMOS ----- //

    const baseBillingNotice = useMemo(() => {
        return formatBillingLockNotice(activeBase, billingRecord, lockReason);
    }, [activeBase, billingRecord, lockReason]);

    // ----- HANDLERS ----- //

    /**
 * Handles locked navigation attempt.
 */

    const handleLockedNavigationAttempt = useCallback(() => {
        setActiveDatabase(null);
        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.HOME);

        if (location.pathname !== '/') {
            navigate('/', { replace: true });
        }
    }, [location.pathname, navigate, setActiveDatabase, setCurrentNode, setCurrentSection, setSelectedLocation]);

    /**
     * handleDatabaseClick
     * 
     * Handles the click event for selecting a database.
     * 
     * @param {Object} database - The database that was clicked.
     */
    /**
 * Handles database click.
 *
 * @param {*} database
 */
    const handleDatabaseClick = useCallback((database, nodeModule = null, options = {}) => {
        if (baseNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        if (!database) return;

        const databaseKey = database.key || '';
        const moduleParam = String(nodeModule || '').trim().toLowerCase();

        setActiveDatabase(database);
        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.NODE);

        if (databaseKey) {
            navigate(buildCollectionPath(databaseKey, {
                module: moduleParam,
                appearAs: options?.appearAsKey,
            }));
        }
    }, [handleLockedNavigationAttempt, navigate, setActiveDatabase, setCurrentNode, setCurrentSection, setSelectedLocation, baseNavigationLocked]);

    // ----- HOOKS ----- //

    /**
     * handleTopLevelActionSelect
     * 
     * Handles the selection of a top-level action from the attachment modal.
     * 
     * @param {string} typeKey - The type key of the selected action.
     */

    const {
        cameraOpen,
        cameraContentType,
        uploadInputRef,
        pendingUploadAccept,
        pendingUploadMultiple,
        handleTopLevelActionSelect,
        handleUploadFileSelect,
        setCameraOpen,
        handleCameraContentCreated,
    } = useTopLevelNodeCreate({
        user,
        activeBaseKey: activeBase?.key || null,
        getType,
        language,
        navigate,
        setCurrentNode,
        setCurrentSection,
        setSelectedLocation,
        setActiveDatabase,
    });

    const isMediaCaptureTypeKey = useCallback((typeKey) => {
        const key = String(typeKey || '').trim().toUpperCase();
        return key === 'ATTACH'
            || key === 'CAMERA'
            || key === 'SNAPSHOT'
            || key === 'RECORDING';
    }, []);

    const createLibraryNodeAndPeek = useCallback(async (typeKey) => {
        const newNode = await handleTopLevelActionSelect(typeKey, {
            module: NODE_MODULE.PRIVATE,
            shouldNavigate: false,
            provisional: true,
        });
        if (newNode?.key) {
            openPeek(newNode, { mode: 'reset' });
        }
        return newNode;
    }, [handleTopLevelActionSelect, openPeek]);

    const {
        modalState: createModalState,
        openers: createModalOpeners,
    } = useBaseCreateModals();

    // Add modal open state (toolbar New)
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const closeAddModalRef = useRef(null);

    const handleBaseCreateCompleted = useCallback(() => {
        closeAddModalRef.current?.();
        setAddModalOpen(false);
    }, []);

    // ----- HANDLERS ----- //

    const handleCloseAddModal = useCallback(() => {
        setAddModalOpen(false);
    }, []);

    const handleAddTypeSelect = useCallback(async (typeKey) => {
        if (isBaseLauncherKey(typeKey)) {
            openBaseLauncher(typeKey, createModalOpeners);
            return;
        }

        setAddModalOpen(false);

        if (baseNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setPendingCreateNodeModule(NODE_MODULE.PRIVATE);

        if (isMediaCaptureTypeKey(typeKey)) {
            handleTopLevelActionSelect(typeKey, { module: NODE_MODULE.PRIVATE });
            return;
        }

        await createLibraryNodeAndPeek(typeKey);
    }, [
        createLibraryNodeAndPeek,
        createModalOpeners,
        handleLockedNavigationAttempt,
        handleTopLevelActionSelect,
        isMediaCaptureTypeKey,
        baseNavigationLocked,
    ]);

    // ----- MEMOS ----- //

    const preferredBaseKeyFromSettings = getActiveBaseKeyFromSettings(userSettings);
    const activeBaseKey = useMemo(() => {
        const key = activeBase?.key || activeBase?.id || preferredBaseKeyFromSettings || '';
        return String(key).trim();
    }, [activeBase?.id, activeBase?.key, preferredBaseKeyFromSettings]);

    const {
        unreadByChannel,
    } = useBaseChannels({
        baseKey: activeBaseKey,
        user,
    });

    const homeAiUpdatesUnread = useMemo(() => {
        const feedKey = buildAiFeedChannelKey(activeBaseKey, user?.key);
        return Boolean(feedKey && unreadByChannel?.[feedKey]);
    }, [activeBaseKey, unreadByChannel, user?.key]);

    const aiSelected = Boolean(
        getAiSessionKeyFromLocation(
            location,
            activeBaseKey,
            user?.key || user?.userKey,
        )
    );

    /**
     * Handles click on the home button in the sidebar, navigating to the home section.
     */
    const handleHomeClick = useCallback(() => {
        setCurrentSection(SectionType.HOME);
        navigate('/');
        onRequestClose?.();
    }, [navigate, onRequestClose, setCurrentSection]);

    /**
     * Opens the Appdeck AI conversation for the current user.
     */
    const handleAiClick = useCallback(() => {
        if (baseNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        const currentUserKey = String(user?.key || user?.userKey || '').trim();
        const aiChannelKey = buildAiFeedChannelKey(activeBaseKey, currentUserKey);
        if (!aiChannelKey) return;

        setActiveDatabase(null);
        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.HOME);
        onRequestClose?.();
        navigate(buildAiHomePath({ channelKey: aiChannelKey, baseKey: activeBaseKey }));
    }, [
        activeBaseKey,
        baseNavigationLocked,
        handleLockedNavigationAttempt,
        navigate,
        onRequestClose,
        setActiveDatabase,
        setCurrentNode,
        setCurrentSection,
        setSelectedLocation,
        user?.key,
        user?.userKey,
    ]);

    /**
 * Handles notifications click.
 */
    const handleNotificationsClick = useCallback(() => {
        if (baseNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.NOTIFICATIONS);
        onRequestClose?.();
        navigate('/notifications');
    }, [handleLockedNavigationAttempt, navigate, onRequestClose, setCurrentNode, setCurrentSection, setSelectedLocation, baseNavigationLocked]);

    // ----- HANDLERS ----- //

    /**
 * Handles profile click.
 */

    const handleProfileClick = useCallback(() => {
        if (baseNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        const currentUserKey = String(user?.key || user?.userKey || '').trim();
        if (!activeBaseKey || !currentUserKey) return;

        setActiveDatabase(null);
        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.USER);
        onRequestClose?.();
        navigate(buildBaseMemberProfilePath(activeBaseKey, currentUserKey));
    }, [activeBaseKey, handleLockedNavigationAttempt, navigate, onRequestClose, setActiveDatabase, setCurrentNode, setCurrentSection, setSelectedLocation, user?.key, user?.userKey, baseNavigationLocked]);

    /**
 * Handles trash click.
 */
    const handleTrashClick = useCallback(() => {
        if (baseNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.TRASH);
        onRequestClose?.();
        navigate('/trash');
    }, [handleLockedNavigationAttempt, navigate, onRequestClose, setCurrentNode, setCurrentSection, setSelectedLocation, baseNavigationLocked]);

    /**
 * Opens Customize (Studio) as a normal base section.
 */
    const handleStudioClick = useCallback(() => {
        if (!canSeeBaseSettings) return;
        if (baseNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.STUDIO);
        onRequestClose?.();
        navigate(getSectionPath(SectionType.STUDIO));
    }, [
        baseNavigationLocked,
        canSeeBaseSettings,
        handleLockedNavigationAttempt,
        navigate,
        onRequestClose,
        setCurrentNode,
        setCurrentSection,
        setSelectedLocation,
    ]);

    /**
 * Handles schedule click.
 */
    const handleScheduleClick = useCallback(() => {
        if (baseNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.SCHEDULE);
        onRequestClose?.();
        navigate('/schedule');
    }, [handleLockedNavigationAttempt, navigate, onRequestClose, setCurrentNode, setCurrentSection, setSelectedLocation, baseNavigationLocked]);

    // ----- MEMOS ----- //

    const unreadNotificationsCount = useMemo(() => {
        if (!activeBaseKey || !Array.isArray(userUnreadNotifications)) return 0;

        return userUnreadNotifications.filter((notification) => {
            if (!notification || notification.isRead === true) return false;

            const notificationBaseKey = String(notification?.baseKey || '').trim();
            return notificationBaseKey === activeBaseKey;
        }).length;
    }, [activeBaseKey, userUnreadNotifications]);

    const hasBaseCover = useMemo(() => {
        return typeof activeBase?.sidebarCoverPhotoUrl === 'string' && activeBase.sidebarCoverPhotoUrl.trim().length > 0;
    }, [activeBase?.sidebarCoverPhotoUrl]);

    // ----- HOOKS ----- //

    const {
        isSectionSelected,
        isOwnProfileSelected,
    } = useSidebarSelection({
        user,
        activeBaseKey,
    });
    if (!user) return null;

    // ----- RENDER ----- //

    return (
        <>
            <div className={[
                'sidebar-container',
                forceVisible ? 'force-visible' : '',
                hideHeader ? 'sidebar-container--shell-panel' : '',
                isSidebarRailCompact ? 'is-rail-compact' : '',
            ].filter(Boolean).join(' ')}
                style={{
                    //backgroundColor: theme.panel
                }}>

                {/* HEADER */}
                {!hideHeader ? (
                <div className="sidebar-header">

                    <ShellHeader
                      beforeSwitcher={hasBaseCover ? (
                        <div className="sidebar-header-cover">
                            <BaseCover
                                assetType="cover"
                                base={activeBase}
                                borderWidth={0}
                                borderRadius={7}
                                height={coverHeight || 200}
                                maxHeight={coverHeight || 200}
                                shape={PhotoShape.SQUARE}
                                width="100%"
                            />
                        </div>
                      ) : null}
                    />

                    {/* HEADER ROW */}
                    <div className="sidebar-header-row">

                        {/* Experimental icon toolbar 
                        <div className="sidebar-icon-toolbar" aria-label="Sidebar shortcuts">
                            {SIDEBAR_ICON_TOOLBAR_ITEMS.map((item) => {
                                if (item.iconKey === 'add' && (hideAddButton || baseNavigationLocked)) {
                                    return null;
                                }

                                const isMode = item.iconKey === 'mode';
                                const iconKey = isMode
                                    ? (isDarkMode ? 'dark_mode' : 'light_mode')
                                    : item.iconKey;
                                const path = isMode
                                    ? (isDarkMode ? DARK_MODE_ICON_PATH : LIGHT_MODE_ICON_PATH)
                                    : undefined;

                                const selected = item.iconKey === 'home'
                                    ? isSectionSelected(SectionType.HOME)
                                    : item.iconKey === 'search'
                                        ? isSectionSelected(SectionType.SEARCH)
                                        : item.iconKey === 'person'
                                            ? isOwnProfileSelected
                                            : item.iconKey === 'event'
                                                ? isSectionSelected(SectionType.SCHEDULE)
                                                : item.iconKey === 'bookmark'
                                                    ? isSectionSelected(SectionType.FAVORITES)
                                                    : false;

                                const enabled = item.iconKey === 'home' || item.iconKey === 'mode'
                                    ? true
                                    : item.iconKey === 'person'
                                        ? (!baseNavigationLocked && Boolean(activeBaseKey && (user?.key || user?.userKey)))
                                        : !baseNavigationLocked;

                                const showHomeUnread = item.iconKey === 'home' && homeAiUpdatesUnread;
                                const iconColor = selected
                                    ? theme.foregroundSelected
                                    : theme.foregroundMuted;

                                const handleClick = () => {
                                    if (!enabled) return;

                                    switch (item.iconKey) {
                                        case 'home':
                                            handleHomeClick();
                                            break;
                                        case 'search':
                                            handleSearchClick();
                                            break;
                                        case 'person':
                                            handleProfileClick();
                                            break;
                                        case 'event':
                                            handleScheduleClick();
                                            break;
                                        case 'bookmark':
                                            handleFavoritesClick();
                                            break;
                                        case 'mode':
                                            handleModeClick();
                                            break;
                                        case 'add':
                                            handleOpenAddModal();
                                            break;
                                        default:
                                            break;
                                    }
                                };

                                return (
                                    <button
                                        key={item.label}
                                        type="button"
                                        className={`sidebar-icon-toolbar-button${selected ? ' selected' : ''}`}
                                        title={item.label}
                                        aria-label={isMode
                                            ? (isDarkMode ? 'Switch to light mode' : 'Switch to dark mode')
                                            : item.label}
                                        aria-current={selected ? 'page' : undefined}
                                        style={{
                                            backgroundColor: selected ? theme.backgroundSelected : undefined,
                                            cursor: enabled ? 'pointer' : 'not-allowed',
                                            opacity: enabled ? 1 : 0.45,
                                        }}
                                        onClick={handleClick}
                                    >
                                        <span className="sidebar-icon-toolbar-icon-wrap">
                                            <Icon
                                                iconKey={iconKey}
                                                path={path}
                                                color={iconColor}
                                                width={20}
                                                height={20}
                                            />
                                            {showHomeUnread ? (
                                                <span
                                                    className="sidebar-icon-toolbar-unread"
                                                    style={{ backgroundColor: theme.foreground }}
                                                    aria-label="Unread AI Updates"
                                                />
                                            ) : null}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        */}

                        <AddModal
                            isOpen={addModalOpen}
                            showFind={false}
                            showBaseLaunchers
                            onClose={handleCloseAddModal}
                            onTypeSelect={handleAddTypeSelect}
                        />

                        {/* Temporarily hidden: create via Capture composer "+" (AddModal) instead */}
                        {/*
                        {!hideAddButton && !baseNavigationLocked && (
                            <AddNodeButton
                                showBaseLaunchers
                                onTypeSelect={handleAttachmentSelect}
                                onAddModalCloseReady={handleAddModalCloseReady}
                            />
                        )}
                        */}


                    </div>

                </div>
                ) : null}

                {/* BILLING ALERT */}
                {baseBillingNotice && !isSidebarRailCompact ? (
                    <div

                        className="sidebar-billing-alert"
                        style={{
                          backgroundColor: baseNavigationLocked
                                ? (theme.warning || theme.backgroundHover)
                                : theme.backgroundHover,
                          borderColor: baseNavigationLocked
                                ? (theme.warning || theme.backgroundHover)
                                : theme.backgroundHover,
                        }}
                    >
                        {baseBillingNotice}
                    </div>
                ) : null}

                {/* BUTTONS */}
                <div className="sidebar-buttons">

                    {/* TOP BUTTONS — mobile only; desktop uses GlobalToolbar */}
                    {isMobile ? (
                    <div className="sidebar-top-buttons">

                        <SidebarButton
                            iconKey="home"
                            label="Home"
                            selected={isSectionSelected(SectionType.HOME) && !aiSelected}
                            rightContent={homeAiUpdatesUnread ? (
                                <span
                                    style={{
                                        backgroundColor: theme.foreground,
                                        borderRadius: '999px',
                                        display: 'inline-block',
                                        flexShrink: 0,
                                        height: 8,
                                        width: 8,
                                    }}
                                    aria-label="Unread AI Updates"
                                />
                            ) : null}
                            onClick={handleHomeClick}
                        />

                        <SidebarButton
                            iconKey={AI_ICON_KEY}
                            iconPath={AI_ICON_PATH}
                            label="AI"
                            enabled={!baseNavigationLocked && Boolean(activeBaseKey && (user?.key || user?.userKey))}
                            selected={aiSelected}
                            onClick={handleAiClick}
                        />

                        <SidebarButton
                            iconKey="person"
                            label="Profile"
                            enabled={!baseNavigationLocked && Boolean(activeBaseKey && (user?.key || user?.userKey))}
                            selected={isOwnProfileSelected}
                            onClick={handleProfileClick}
                        />

                        <SidebarButton
                            iconKey="notifications"
                            label="Notifications"
                            enabled={!baseNavigationLocked}
                            selected={isSectionSelected(SectionType.NOTIFICATIONS)}
                            rightContent={unreadNotificationsCount > 0 ? (
                                <div

                                    className="sidebar-notification-badge"
                                    style={{
                                      color: theme.highlightForeground || theme.foreground,
                                    }}
                                >
                                    {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                                </div>
                            ) : null}
                            onClick={handleNotificationsClick}
                        />

                        <SidebarButton
                            iconKey="event"
                            label="Schedule"
                            enabled={!baseNavigationLocked}
                            selected={isSectionSelected(SectionType.SCHEDULE)}
                            onClick={handleScheduleClick}
                        />

                        <SidebarButton
                            iconKey="person_add"
                            label="Invite"
                            enabled={canInviteMembers}
                            onClick={() => {
                              onRequestClose?.();
                              openBaseInvite();
                            }}
                        />

                        {/*
                        {showUpgradeButton && (
                            <SidebarButton
                                iconKey="arrow_warm_up"
                                label="Upgrade"
                                onClick={() => openBasePlanSettings()}
                            />
                        )}
                        */}

                        {canOpenSettings && (
                            <SidebarButton
                                iconKey="settings"
                                label="Settings"
                                onClick={() => {
                                  onRequestClose?.();
                                  openBaseSettings();
                                }}
                            />
                        )}

                        <SidebarButton
                            iconKey="help"
                            iconPath={HELP_ICON_PATH}
                            label="Help"
                            onClick={() => {
                              onRequestClose?.();
                              setHelpOpen(true);
                            }}
                        />

                        {canSeeBaseSettings && !isMobile ? (
                            <SidebarButton
                                iconKey="code"
                                label="Customize"
                                enabled={!baseNavigationLocked}
                                selected={isSectionSelected(SectionType.STUDIO)}
                                onClick={handleStudioClick}
                            />
                        ) : null}

                        <SidebarButton
                            iconKey="delete"
                            label="Trash"
                            enabled={!baseNavigationLocked}
                            selected={isSectionSelected(SectionType.TRASH)}
                            onClick={handleTrashClick}
                        />

                    </div>
                    ) : null}

                    {!baseNavigationLocked ? (
                        <SidebarBuckets
                            onDatabaseClick={handleDatabaseClick}
                            onRequestClose={onRequestClose}
                            onAddDashboard={createModalOpeners.openDashboard}
                            onAddChannel={createModalOpeners.openChannel}
                            onCreateMessage={createModalOpeners.openMessage}
                            onAddDatabase={createModalOpeners.openDatabase}
                        />
                    ) : null}

                    <SidebarBucketsModals
                      modalState={createModalState}
                      onCreated={handleBaseCreateCompleted}
                    />

                </div>

                {!hideHeader ? (
                    <>
                        <Divider
                            margin="10px"
                        />

                        {/* FOOTER ROW */}
                        <div className="sidebar-footer-row">

                            <UserButton />

                        </div>
                    </>
                ) : null}

            </div>

            <input
                style={{
                display: 'none',
              }}
              ref={uploadInputRef}
              type="file"
              accept={pendingUploadAccept}
              multiple={pendingUploadMultiple}
              onChange={handleUploadFileSelect}
            />

            <CameraModal
                isOpen={cameraOpen}
                setOpen={setCameraOpen}
                contentType={cameraContentType}
                onContentCreated={handleCameraContentCreated}
            />

            <HelpPanel
                isOpen={helpOpen}
                typeKey={helpTypeKey}
                typeLabel={helpTypeLabel}
                onClose={() => setHelpOpen(false)}
            />

        </>
    );
});

// Dev Tools
Sidebar.displayName = 'Sidebar';

// Export

export default Sidebar;
